import { useEffect, useRef, useState as useStateRaw, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Seo from '@/components/seo/Seo';
import HeroSection from './components/HeroSection';
import TrainingMeaning from './components/TrainingMeaning';
import FourDomains from './components/FourDomains';
import TimeAndCost from './components/TimeAndCost';
import StudyFormats from './components/StudyFormats';
import ChoosingChecklist from './components/ChoosingChecklist';
import CareerPaths from './components/CareerPaths';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import RelatedPages from './components/RelatedPages';

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useStateRaw(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

function renderTranslatedParagraphs(text: string, className: string): ReactNode {
  return text.split(/\n{2,}/).map((paragraph, index) => (
    <p key={index} className={className}>
      {paragraph}
    </p>
  ));
}

export default function ButlerTrainingSchoolPage() {
  const { lang } = useLanguageContext();
  const { t } = useTranslation();

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: lang === 'en' ? 'Home' : 'ホーム',
        item: 'https://butler-academy.jp/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: lang === 'en' ? 'What Is a Butler Training School?' : '執事養成学校とは？',
        item: 'https://butler-academy.jp/butler-training-school',
      },
    ],
  };

  return (
    <>
      <Header />
      <main>
        <Seo route="/butler-training-school" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
        />

        <HeroSection />

        <FadeInSection>
          <TrainingMeaning />
        </FadeInSection>

        <FadeInSection>
          <FourDomains />
        </FadeInSection>

        <FadeInSection>
          <TimeAndCost />
        </FadeInSection>

        <FadeInSection>
          <StudyFormats />
        </FadeInSection>

        <FadeInSection>
          <ChoosingChecklist />
        </FadeInSection>

        <FadeInSection>
          <CareerPaths />
        </FadeInSection>

        <FadeInSection>
          <FAQSection />
        </FadeInSection>

        <FadeInSection>
          <CTASection />
        </FadeInSection>

        <FadeInSection>
          <RelatedPages />
        </FadeInSection>

        <div className="bg-background-50 py-8">
          {renderTranslatedParagraphs(
            t('butlerTrainingSchool.lastUpdated'),
            'text-center text-[13px] text-foreground-400 tracking-[0.05em]'
          )}
        </div>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={t('common.backToTop')}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}
